import { useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { ownerInviteApi } from '@/api/ownerInviteApi';
import { devError } from '@/utils/devLog';

/**
 * Self-healing bootstrap for a stuck raise-owner claimant.
 *
 * The campaign-invite funnel ({@link InviteClaim}) stamps the `raise_owner` role on
 * accept and CONFIRMS it (reconcile-role + claims refresh) before routing. But the
 * invite is SINGLE-USE: if the inline stamp AND every in-funnel retry fail (e.g. a
 * persistent Firebase-write outage) and the user abandons the page, the invite is
 * already consumed — there is no second funnel pass to re-trigger the stamp, and the
 * VERIFIED claimant is left without owner access until an admin intervenes.
 *
 * This hook closes that gap independently of the funnel page: on each fresh
 * authenticated session it fires the IDEMPOTENT `reconcile-role` endpoint ONCE for a
 * signed-in user who is not already an owner/admin. The server short-circuits cheaply
 * for a non-claimant (no VERIFIED `ar_project_claims` row → `isRaiseOwner:false`, no
 * stamp), so the call is a no-op for the overwhelming majority of users; for a stuck
 * claimant it stamps the role and invalidates the backend auth cache, and we force a
 * claims refresh so `AuthManager` flips `isRaiseOwner`. The App layout split then
 * resolves them into the OwnerLayout and routes them to `/owner` on the next render.
 *
 * Properties that keep it safe:
 *   • FIRE-AND-FORGET — runs inside an effect and never blocks app render; a transport
 *     failure is swallowed (the next session re-attempts).
 *   • AT MOST ONCE per uid per session — a module-scoped guard prevents a request storm
 *     across re-renders/remounts (and the React 18 StrictMode double-invoke).
 *   • NO REDIRECT LOOP — a genuine non-claimant gets `isRaiseOwner:false` and nothing
 *     changes (no refresh, no nav); a healed claimant flips `isRaiseOwner:true`, which
 *     closes the gate below so the hook never fires again.
 */

// Module-scoped guard: the reconcile is attempted at most once per uid per app
// session (page load), surviving incidental remounts of the host component and the
// StrictMode double-mount. A new page load starts with an empty set, so a session
// whose reconcile failed transiently re-attempts on the next login.
const attemptedUids = new Set<string>();

export function useOwnerRoleBootstrap(): void {
  const { user, isLoading, isRaiseOwner, isAdmin, refreshClaims } = useAuth();
  // Hold the latest `refreshClaims` in a ref so the effect does not re-run on its
  // (per-render) identity — the effect is keyed purely off the auth GATE inputs.
  const refreshClaimsRef = useRef(refreshClaims);
  refreshClaimsRef.current = refreshClaims;

  const uid = user?.uid ?? null;

  useEffect(() => {
    // GATE: only a fully-resolved, signed-in, non-owner, non-admin session is a
    // candidate for healing. Owners already hold the role; admins are a distinct
    // persona — neither needs (nor should trigger) a reconcile.
    if (isLoading || !uid || isRaiseOwner || isAdmin) return;
    // Claim the attempt BEFORE the async call so concurrent renders / the StrictMode
    // double-invoke cannot double-fire it.
    if (attemptedUids.has(uid)) return;
    attemptedUids.add(uid);

    void (async () => {
      try {
        const result = await ownerInviteApi.reconcileRole();
        // Only a server verdict that GRANTS owner access warrants a claims refresh; a
        // non-claimant (the common case) leaves the session untouched — no refresh, no
        // navigation, no loop. `refreshClaims` mutates the AuthManager singleton, so
        // it is safe even if the host has since unmounted.
        if (result.isRaiseOwner) {
          await refreshClaimsRef.current();
        }
      } catch (err) {
        // Fire-and-forget: a transport failure must never disrupt the app shell.
        devError('useOwnerRoleBootstrap: reconcile-role failed', err);
      }
    })();
  }, [uid, isLoading, isRaiseOwner, isAdmin]);
}
